import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { RMS_BOOTSTRAP_QUERY_KEY } from "./bootstrap";

// API base URL
const API_BASE = BASE_API_URL_V2;

// ============= Types =============

export interface DocumentColumn {
  id: number;
  name: string;
  position: number;
  data_type: "string" | "number" | "percentage" | "boolean" | "date" | "entity";
  description?: string | null;
  is_enum: boolean;
  scalar_values?: string[] | null;
  enum_colors?: Record<string, string> | null;
  entity_types?: string[] | null;
  is_array: boolean;
  ai_autofill: boolean;
  default_value?: unknown | null;
}

export interface DocumentColumnSchema {
  columns: DocumentColumnDefinition[];
}

export interface DocumentColumnDefinition {
  id?: number | null;
  name: string;
  position: number;
  data_type: "string" | "number" | "percentage" | "boolean" | "date" | "entity";
  description?: string | null;
  is_enum: boolean;
  scalar_values?: string[] | null;
  enum_colors?: Record<string, string> | null;
  entity_types?: string[] | null;
  is_array: boolean;
  ai_autofill: boolean;
  default_value?: unknown | null;
}

// ============= API Functions =============

export const fetchDocumentColumns = async (
  organizationId?: string
): Promise<DocumentColumn[]> => {
  const params = organizationId
    ? new URLSearchParams({ organization_id: organizationId })
    : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/documents/columns?${params}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) throw new Error("Failed to fetch document columns");
  return response.json();
};

export const updateDocumentColumns = async (
  payload: DocumentColumnSchema,
  organizationId?: string
): Promise<DocumentColumn[]> => {
  const params = organizationId ? `?organization_id=${organizationId}` : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/documents/columns${params}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) throw new Error("Failed to update document columns");
  return response.json();
};

// ============= React Query Hooks =============

export const DOCUMENT_COLUMNS_QUERY_KEYS = {
  all: ["document-columns"] as const,
  columns: (organizationId?: string) =>
    [...DOCUMENT_COLUMNS_QUERY_KEYS.all, "columns", organizationId] as const,
};

export const useDocumentColumns = (organizationId?: string) => {
  return useQuery({
    queryKey: DOCUMENT_COLUMNS_QUERY_KEYS.columns(organizationId),
    queryFn: () => fetchDocumentColumns(organizationId),
    staleTime: 60 * 1000, // 1 minute
  });
};

export const useUpdateDocumentColumns = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      organizationId,
    }: {
      payload: DocumentColumnSchema;
      organizationId?: string;
    }) => updateDocumentColumns(payload, organizationId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_COLUMNS_QUERY_KEYS.columns(variables.organizationId),
      });
      // The document schema the bootstrap carries is built from these columns.
      queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY });
    },
  });
};
